import { getHealthCareOrgName } from '@src/utility/common.service';
import {
  checkIsInternal,
  checkIsSimulationMode,
  checkHCO,
  checkIsExternal
} from '@src/utility/createStorage';
import React, { useCallback, useEffect, useState } from 'react';
import { useImpactNavigation } from '@src/CommonComponents/ImpactLinkButton/useImpactNavigation';
import {
  getExternalProvidersCount,
  getFilteredMenus,
  getFilteredPSMSMenus,
  getFilteredSecondaryMenus,
  getPSMSFilteredSecondaryMenus,
} from './NavigationBar.menus';
import { styles } from './NavigationBar.styles';
import { fetchTaxIdDropdownData, getInitialSelectedTaxId, invalidateTinCache, updateSelectedTaxId, TinData } from './NavigationData';
import { ROUTES_PATHS } from '@src/constants/routes';
import { NAV_ITEM_NAMES } from './constant';
import { fetchPSMSFeatureFlag, fetchPSMSTinAssociationCheck } from '@src/middleware/services/psms/psms.service';
import { SESSION_KEYS } from '@src/constants/featureFlags';
import { useMenuRoles } from './useMenuRoles';
import { APP_EVENTS } from '@src/constants/app-shell-constants';
interface NavigationBarProps {
  onNavItemClick?: (path: string, itemName: string) => void;
  showSearch?: boolean;
  filterOptions?: {
    isInternal?: boolean;
    isMultiTin?: boolean;
    userRoles?: string[];
    disabledKeys?: string[];
  };
}

export const MOBILE_NAV_BREAKPOINT = 768;

const isSmallViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= MOBILE_NAV_BREAKPOINT;

const parseSessionJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const GEO_HISTORY_KEY = 'navGeoCriteriaByPath';

const readGeoHistory = (): Record<string, string> => {
  try {
    return JSON.parse(sessionStorage.getItem(GEO_HISTORY_KEY) || '{}');
  } catch {
    return {};
  }
};

const recordGeoCriteriaForPath = (pathname: string, value: string | null) => {
  const history = readGeoHistory();
  history[pathname] = value ?? 'null';
  sessionStorage.setItem(GEO_HISTORY_KEY, JSON.stringify(history));
};

const getGeoCriteriaForPath = (pathname: string): string | null => {
  const history = readGeoHistory();
  return Object.prototype.hasOwnProperty.call(history, pathname) ? history[pathname] : null;
};

const normalizeTinData = (tin: any): TinData | null => {
  if (!tin) return null;

  const value = tin.value || tin.Tin || tin.TIN || '';
  const name = tin.name || tin.TinName || tin.TINNAME || '';

  if (!value) {
    return null;
  }

  return {
    value,
    name: name || 'Tax ID Name not available'
  };
};

const NavigationBar: React.FC<NavigationBarProps> = ({
  onNavItemClick,
  showSearch = true,
  filterOptions,
}) => {
  const { navigateImpact, ImpactModal } = useImpactNavigation();

  // sessionStorage.setItem('state', JSON.stringify(state));
  // sessionStorage.setItem('GeoCriteria', 'null'); // GEO CRITERIA ACTIVE - Shows 6 menus in first bar, Reporting View + Change Providers in second bar
  // sessionStorage.setItem('GeoCriteria', JSON.stringify(null)); // GEO CRITERIA INACTIVE - Shows all menus
  const geoCriteria = sessionStorage.getItem('GeoCriteria');
  const geoSegmentFlag = geoCriteria !== 'null' && geoCriteria !== null;
  const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  const groupNameFromSession = sessionStorage.getItem('groupName');
  const isContractIdNav = sessionStorage.getItem('contractIdNavigation') === 'true';
  const isInternal = checkIsInternal();
  const loginType = isInternal ? 'Internal' : 'External';

  // Use ref to track the selected item name (for popup scenarios)
  const selectedNameRef = React.useRef<string>('Provider Performance');

  // Determine initial selectedView based on current URL, saved dropdown value, and GeoCriteria
  // Matches Angular's router subscription logic (hamburger-menu.component.ts lines 1323-1380)
  const getInitialView = () => {
    const currentGeoCriteria = sessionStorage.getItem('GeoCriteria');

    // Priority 1: Check current URL to match route-based view
    const currentPath = window.location.pathname;
    const routeToViewMap: Record<string, string> = {
      [ROUTES_PATHS.ContractIntelligence.Scorecard]: 'Contract Intelligence',
      '/medical-cost': 'Affordability Drivers',
      [ROUTES_PATHS.Operations]: 'Operations',
      [ROUTES_PATHS.Providers.Briefing]: 'Provider Negotiation Briefing',
      [ROUTES_PATHS.Providers.Experience]: 'Provider Experience',
      '/overview-page-advocate/health-system-details': 'Provider Performance'
    };

    // Check if current path matches any route - this ensures menus render correctly from the start
    for (const [route, viewName] of Object.entries(routeToViewMap)) {
      if (currentPath === route || (currentPath.startsWith(route + '/') && route !== '/')) {

        // Angular Logic: If navigating to Affordability Drivers with active GeoCriteria (MARKET/REGION/STATE)
        // → return "Geographic Segmentation" instead
        if (viewName === 'Affordability Drivers' &&
          currentGeoCriteria !== null &&
          currentGeoCriteria !== 'null' &&
          (currentGeoCriteria === 'MARKET' || currentGeoCriteria === 'REGION' || currentGeoCriteria === 'STATE')) {
          return 'Geographic Segmentation';
        }

        // Angular Logic: If navigating to Contract Intelligence with GeoCriteria = MARKET
        // → return "Geographic Segmentation" instead
        if (viewName === 'Contract Intelligence' && currentGeoCriteria === 'MARKET') {
          return 'Geographic Segmentation';
        }

        // Default: Return the matched view name
        return viewName;
      }
    }

    // Priority 2: Check GeoCriteria flag
    if (geoSegmentFlag) {
      return 'Geographic Segmentation'; // Use display name for menu filtering
    }

    // Priority 3: Restore selected view from sessionStorage to maintain state after page reload
    const savedDropdownValue = sessionStorage.getItem(
      'selectedProviderPerformance'
    );
    if (savedDropdownValue) {
      // Return the saved value directly - display names ARE used for menu filtering
      return savedDropdownValue;
    }

    // Priority 4: Default fallback
    return 'Provider Performance'; // Default view (use display name)
  };

  // State for selected view (like hamburger-menu's selectedView)
  const [selectedView, setSelectedView] = useState<string>(getInitialView());

  // State to track simulation mode changes
  const [isSimulationMode, setIsSimulationMode] = useState<boolean>(
    sessionStorage.getItem('simulationMode') === 'true'
  );

  // State to track activeProviderMode from Angular (mimics hamburger-menu's activeProviderMode)
  // This is updated via ExitProviderView event and determines if we're in simulation mode
  const [activeProviderMode, setActiveProviderMode] = useState<boolean>(
    sessionStorage.getItem('simulationMode') === 'true'
  );

  // State to track pending view change (for Provider Performance/Geographic Segmentation)
  // When these views are selected, we don't update immediately - we wait for popup to close
  const [pendingViewChange, setPendingViewChange] = useState<string | null>(
    null
  );

  // State to store the previous selectedProviderPerformance before opening popup
  // Used to revert if popup is closed without selection
  const [previousProviderPerformance, setPreviousProviderPerformance] =
    useState<string | null>(null);

  // const [searchValue, setSearchValue] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState<boolean>(isSmallViewport);

  // Track last clicked item to prevent redundant actions
  const [lastClickedItem, setLastClickedItem] = useState<{
    path: string;
    itemName: string;
    clickedAt: number;
  } | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = isSmallViewport();
      setIsMobileNav(nextIsMobile);
      if (!nextIsMobile) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleOpenResponsiveMenu = () => {
      if (!isSmallViewport()) return;
      setIsMobileNav(true);
      setMobileDrawerOpen(true);
    };

    window.addEventListener(APP_EVENTS.OPEN_RESPONSIVE_HAM_MENU, handleOpenResponsiveMenu);
    return () => {
      window.removeEventListener(APP_EVENTS.OPEN_RESPONSIVE_HAM_MENU, handleOpenResponsiveMenu);
    };
  }, []);

  // Initialize selectedProviderPerformance from URL, sessionStorage, or default
  // Matches Angular's router subscription logic (hamburger-menu.component.ts lines 1323-1380)
  const getInitialProviderPerformance = () => {
    const currentGeoCriteria = sessionStorage.getItem('GeoCriteria');

    // Priority 1: Check current URL to match route-based view (same logic as getInitialView)
    const currentPath = window.location.pathname;
    const routeToViewMap: Record<string, string> = {
      [ROUTES_PATHS.ContractIntelligence.Scorecard]: 'Contract Intelligence',
      '/medical-cost': 'Affordability Drivers',
      [ROUTES_PATHS.Operations]: 'Operations',
      [ROUTES_PATHS.Providers.Briefing]: 'Provider Negotiation Briefing',
      [ROUTES_PATHS.Providers.Experience]: 'Provider Experience',
      '/overview-page-advocate/health-system-details': 'Provider Performance'
    };

    // Check if current path matches any route
    for (const [route, viewName] of Object.entries(routeToViewMap)) {
      if (currentPath === route || (currentPath.startsWith(route + '/') && route !== '/')) {

        // Angular Logic: If navigating to Affordability Drivers with active GeoCriteria (MARKET/REGION/STATE)
        // → return "Geographic Segmentation" instead
        if (viewName === 'Affordability Drivers' &&
          currentGeoCriteria !== null &&
          currentGeoCriteria !== 'null' &&
          (currentGeoCriteria === 'MARKET' || currentGeoCriteria === 'REGION' || currentGeoCriteria === 'STATE')) {
          return 'Geographic Segmentation';
        }

        // Angular Logic: If navigating to Contract Intelligence with GeoCriteria = MARKET
        // → return "Geographic Segmentation" instead
        if (viewName === 'Contract Intelligence' && currentGeoCriteria === 'MARKET') {
          return 'Geographic Segmentation';
        }

        // Default: Return the matched view name
        return viewName;
      }
    }

    // Priority 2: Check sessionStorage
    const savedView = sessionStorage.getItem('selectedProviderPerformance');
    if (savedView) {
      return savedView;
    }

    // Priority 3: Default based on GeoCriteria
    return !geoSegmentFlag ? 'Provider Performance' : 'Geographic Segmentation';
  };

  const [selectedProviderPerformance, setSelectedProviderPerformance] =
    useState<string>(getInitialProviderPerformance());

  // Multi-TIN and group name logic (like hamburger-menu)
  const isMultiTin = multiTinNavigation === 'true';
  const isExtendedMultiTin =
    isMultiTin && multiSelectedTins && multiSelectedTins.split(',').length > 1;
  const isSingleTinInMultiTinFlow =
    isMultiTin &&
    multiSelectedTins &&
    multiSelectedTins.split(',').length === 1;
  let groupName = groupNameFromSession
    ? groupNameFromSession
    : 'Custom Named List';
  if (groupName.toLowerCase() === 'unsaved') {
    groupName = 'Custom Named List';
  }

  // Handle groupName for single TIN in multi-TIN flow (like hamburger-menu logic at line 1298-1303)
  if (isSingleTinInMultiTinFlow) {
    const multiSelectedTinsGC = sessionStorage.getItem('multiSelectedTinsGC');
    if (multiSelectedTinsGC) {
      const tinsGCArray = JSON.parse(multiSelectedTinsGC);
      if (groupName === 'Custom Named List' && tinsGCArray && tinsGCArray[0]) {
        groupName = tinsGCArray[0].TINNAME;
      }
    }
  }

  // Single Tax ID display logic (like hamburger-menu lines 202, 223-230, 1498)
  const [singleViewTaxId, setSingleViewTaxId] = useState<string>('');

  const [isPSMSTinAssociated, setIsPSMSTinAssociated] = useState<boolean>(false);

  const getSessionExternalTin = (): string | undefined => {
    try {
      const parsedState = JSON.parse(sessionStorage.getItem('state') || '{}');
      return (
        parsedState?.uhc?.selectedCorporateTin?.value ||
        undefined
      );
    } catch {
      return undefined;
    }
  };

  // Check if we should show single TaxId (like hamburger-menu showSingleTaxId getter at line 223)
  const showSingleTaxId = (() => {
    const isInternal = checkIsInternal();
    // Check environment.extendedTinLevelExperience equivalent
    const extendedTinLevelExperience = true; // This should match your environment config

    return (
      isInternal &&
      extendedTinLevelExperience &&
      sessionStorage.getItem('multiTinNavigation') === 'true' &&
      multiSelectedTins &&
      multiSelectedTins.split(',').length === 1
    );
  })();

  // Set singleViewTaxId when conditions are met (like hamburger-menu line 1498)
  useEffect(() => {

    const notGeoFlow = (geoCriteria === 'null' || geoCriteria === null);
    const stateData = parseSessionJson<any>('state', {});

    if ((showSingleTaxId && multiSelectedTins) || (checkHCO() && notGeoFlow)) {
      // Derive the effective TIN once with HCO fallback -> multiSelectedTins
      let effectiveTin = (checkHCO() && notGeoFlow)
        ? stateData?.uhc?.selectedCorporateTin?.value
        : multiSelectedTins?.split(',')[0];

      if (!effectiveTin && checkHCO() && notGeoFlow) {
        effectiveTin = stateData?.tinsInfo?.[0]?.value;
        const firstTin = stateData?.tinsInfo?.[0];
        if (firstTin) {
          updateSelectedTaxId({ name: firstTin['name'] || 'Tax ID Name not available', value: firstTin['value'] || '' });
        }
      }
      if (effectiveTin) {
        setSingleViewTaxId(effectiveTin);
      }

      //PSMS tin association check and set the flag to session storage to be used across the application
      if (isSimulationMode && ((effectiveTin && notGeoFlow) || (checkHCO() && notGeoFlow))) {
        fetchPSMSFeatureFlag().then(flagValue => {
          sessionStorage.setItem(SESSION_KEYS.PSMS_FEATURE_FLAG, flagValue);
          if (flagValue === 'Y') {
            fetchPSMSTinAssociationCheck(effectiveTin)
              .then(dataToCheck => {
                setIsPSMSTinAssociated(!!dataToCheck);
                sessionStorage.setItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED, JSON.stringify(!!dataToCheck));
              })
              .catch(error => {
                console.error('[NavigationBar] Error fetching PSMS TIN association:', error);
              });
          }
        })
      }
    } else {
      sessionStorage.removeItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED);
    }
  }, [showSingleTaxId, multiSelectedTins, isSimulationMode, loginType]);


  //External User - handles refresh scenario where TIN is already in sessionStorage on mount.
  // First-load scenario (TIN not yet available) is handled by SelectedCorporateTinUpdate listener below.
  useEffect(() => {
    if (loginType === "External") {
      const externalTin = getSessionExternalTin();

      if (externalTin) {
        setSingleViewTaxId(externalTin);
        fetchPSMSFeatureFlag().then(flagValue => {
          sessionStorage.setItem(SESSION_KEYS.PSMS_FEATURE_FLAG, flagValue);
          if (flagValue === 'Y') {
            fetchPSMSTinAssociationCheck(externalTin)
              .then(dataToCheck => {
                setIsPSMSTinAssociated(!!dataToCheck);
                sessionStorage.setItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED, JSON.stringify(!!dataToCheck));
              })
              .catch(error => {
                console.error('[NavigationBarExternal] Error fetching PSMS TIN association:', error);
              });
          }
        });
      } else {
        sessionStorage.removeItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED);
      }
    }
  }, [])


  // Initialize selected TIN state using lazy initialization
  // The function runs once when state is created, avoiding stale initial values
  const [selectedTinLevelData, setSelectedTinLevelData] = useState<{ value: string; name: string } | null>(() => {
    return getInitialSelectedTaxId();
  });
  const syncSimulationTinSelection = useCallback(() => {
    const selectedTinValues = (sessionStorage.getItem('multiSelectedTins') || '')
      .split(',')
      .map((tin) => tin.trim())
      .filter(Boolean);

    const multiTinNavigationEnabled = sessionStorage.getItem('multiTinNavigation') === 'true';
    const selectedMultiTinValue =
      multiTinNavigationEnabled && selectedTinValues.length === 1
        ? selectedTinValues[0]
        : '';

    const multiSelectedTinsGC = parseSessionJson<any[]>('multiSelectedTinsGC', []);
    const providerTinInfo = parseSessionJson<any[]>('providerTinInfo', []);
    const stateData = parseSessionJson<any>('state', {});
    const currentUser = parseSessionJson<any[]>('currentUser', []);

    const preferredTin = selectedMultiTinValue
      ? normalizeTinData(
        multiSelectedTinsGC.find((tin) => (tin?.TIN || tin?.Tin || tin?.value) === selectedMultiTinValue) ||
        providerTinInfo.find((tin) => (tin?.TIN || tin?.Tin || tin?.value) === selectedMultiTinValue) ||
        stateData?.journeyExtendedTinSelected?.taxId?.find(
          (tin: any) => (tin?.TIN || tin?.Tin || tin?.value) === selectedMultiTinValue
        ) ||
        stateData?.taxId?.find((tin: any) => (tin?.TIN || tin?.Tin || tin?.value) === selectedMultiTinValue) ||
        currentUser?.[0]?.selectedTin ||
        { value: selectedMultiTinValue, name: 'Tax ID Name not available' }
      )
      : normalizeTinData(selectedTinLevelData) ||
      normalizeTinData(currentUser?.[0]?.selectedTin) ||
      normalizeTinData(stateData?.selectedCorporateTin) ||
      normalizeTinData(stateData?.uhc?.selectedCorporateTin);

    if (!preferredTin?.value) {
      return;
    }

    invalidateTinCache();
    updateSelectedTaxId(preferredTin);
    setSelectedTinLevelData(preferredTin);
    setSingleViewTaxId(preferredTin.value);

    window.dispatchEvent(
      new CustomEvent('SelectedCorporateTinUpdate', {
        detail: preferredTin,
      })
    );
    window.dispatchEvent(
      new CustomEvent('TaxIDSyncAng', {
        detail: preferredTin,
      })
    );
  }, [selectedTinLevelData]);
  const lastEmittedPSMSTinRef = React.useRef<string | null>(null);

  const getCurrentPSMSFilterTinValue = () => {
    try {
      const raw = sessionStorage.getItem('psmsFilterState');
      const parsed = raw ? JSON.parse(raw) : null;
      const tins = parsed?.currentFilters?.tin;
      if (Array.isArray(tins) && tins.length > 0) {
        return tins[0]?.value || tins[0]?.Tin || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const emitPSMSTinChangedIfNeeded = (tin: { value: string; name: string }) => {
    if (!tin?.value) return;

    const currentFilterTin = getCurrentPSMSFilterTinValue();
    if (currentFilterTin === tin.value) return;
    if (lastEmittedPSMSTinRef.current === tin.value) return;

    lastEmittedPSMSTinRef.current = tin.value;
    window.dispatchEvent(new CustomEvent('PSMSTinChanged', {
      detail: {
        value: tin.value,
        name: tin.name
      }
    }));
  };

  // REMOVED: usePcorData hook - not needed since we read directly from sessionStorage
  // Reading pcorActive directly from sessionStorage to avoid unnecessary hook calls and re-renders
  const pcorDataStr = JSON.parse(sessionStorage.getItem('pcor') || 'null');
  const pcorActive = pcorDataStr?.Active;
  // Role flags derived directly from sessionStorage — no Angular event needed
  const menuRoles = useMenuRoles();
  const [psmsLob, setPsmsLob] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem('psmsFilterState');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed?.currentFilters?.lob?.value || 'All';
      }
    } catch { /* ignore */ }
    return 'All';
  });

  // Track multi-TIN state for PSMS nav — covers both:
  //   1. PSMS TIN-tab flow: multiTinNavigation = 'true' with >1 TINs
  //   2. HCO flow: parent entity selected, multiple TINs active in PSMS filter
  const [psmsMultiTin, setPsmsMultiTin] = useState<boolean>(() => {
    try {
      if (sessionStorage.getItem('multiTinNavigation') === 'true') {
        const tins = sessionStorage.getItem('multiSelectedTins') || '';
        if (tins.split(',').length > 1) return true;
      }
      const saved = sessionStorage.getItem('psmsFilterState');
      if (saved) {
        const parsed = JSON.parse(saved);
        const tins = parsed?.currentFilters?.tin || [];
        return Array.isArray(tins) && tins.length > 1;
      }
    } catch { /* ignore */ }
    return false;
  });

  const [extendedTin, setExtendedTin] = useState<boolean>(false);

  // Track current pathname to detect navigation changes (especially for PSMS page)
  const [currentPathname, setCurrentPathname] = useState<string>(() => window.location.pathname);

  // Helper function to build complete filter options with roles and data flags
  // Wrapped in useCallback to prevent re-renders
  const buildFilterOptions = useCallback(() => {
    // Role flags derived directly from sessionStorage via useMenuRoles hook
    const roles = menuRoles;

    // Determine loginType and modeType
    const isInternal = checkIsInternal();
    const loginType = isInternal ? 'Internal' : 'External';
    const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';
    const modeType = isSimulationMode ? 'Simulation' : 'General';

    // Get externalProvidersCount for External users
    const externalProvidersCount = getExternalProvidersCount();

    return {
      ...filterOptions,
      selectedView: selectedView,
      geoSegmentFlag: geoSegmentFlag,
      isMultiTin: isMultiTin,
      geoCriteria: geoCriteria,
      loginType: loginType, // Internal or External
      modeType: modeType, // Simulation or General
      pcorData: roles.pcorData, // PCOR data availability from Angular
      admData: roles.admData,
      admRole: roles.admRole,
      scoreCardRole: roles.scoreCardRole,
      pemRole: roles.pemRole,
      affData: roles.affData,
      cesfData: roles.cesfData,
      pocaEnabled: roles.pocaEnabled, // VIM status — still from Angular until VIM check is migrated
      externalProvidersCount: externalProvidersCount, // Multiple providers available
    };
  }, [
    filterOptions,
    selectedView,
    geoSegmentFlag,
    isMultiTin,
    geoCriteria,
    menuRoles,
    isPSMSTinAssociated
  ]);

  const isPSMSRoute = (path: string) => {
    const normalizedPath = (path || '').toLowerCase();
    return normalizedPath === '/psms' || normalizedPath.startsWith('/psms/');
  };

  const isPSMSLandingPage = () => {
    return (window.location.pathname || '').toLowerCase() === '/psms';
  };

  // Helper function to determine if we're on PSMS page
  const isPSMSPage = () => {
    return isPSMSRoute(window.location.pathname);
  };

  // Helper function to get appropriate secondary menus based on current route
  const getSecondaryMenus = (options: any) => {
    if (isPSMSPage()) {
      const isInternal = checkIsInternal();
      const loginType = isInternal ? 'Internal' : 'External';
      const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';

      return getPSMSFilteredSecondaryMenus({
        ...options,
        isPSMSPage: true,
        loginType: loginType,
        modeType: isSimulationMode ? 'Simulation' : 'General'
      });
    } else {
      return getFilteredSecondaryMenus(options);
    }
  };

  // Helper function to set primary nav items based on current route
  const updatePrimaryNavItems = (options: any) => {
    if (isPSMSPage()) {
      const isInternal = checkIsInternal();
      const loginType = isInternal ? 'Internal' : 'External';
      const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';
      const modeType = isSimulationMode ? 'Simulation' : 'General';
      const isPSMSReportCardClicked = sessionStorage.getItem('isPSMSReportCardClicked') === 'true';
      // Don't show PSMS menus for External users or in Simulation mode
      if ((loginType === 'External' || modeType === 'Simulation') && isPSMSLandingPage() && !isPSMSReportCardClicked) {
        setPrimaryNavItems(getFilteredMenus(options));
        return;
      }

      let lobDisabledKeys = [
        ...(psmsLob !== 'All' ? ['payment-integrity'] : []),
        ...(psmsLob === 'EXC' ? ['chat-call'] : []),
        ...(psmsMultiTin ? ['digital-adoption', 'digital-adoption-summary'] : []),
      ];

      if (extendedTin) {
        lobDisabledKeys = [
          ...(psmsLob !== 'All' ? ['payment-integrity'] : []),
          ...(psmsLob === 'EXC' ? ['chat-call'] : []),
          ...(!extendedTin ? ['digital-adoption', 'digital-adoption-summary'] : []),
        ];
      }

      const psmsMenus = getFilteredPSMSMenus({
        loginType,
        modeType,
        disabledKeys: [...lobDisabledKeys, ...(options.disabledKeys || [])]
      });
      if (!psmsMultiTin || extendedTin) {
        psmsMenus.forEach((menu: any) => {
          if (menu.key === 'digital-adoption') {
            menu.disabled = false;
            if (Array.isArray(menu.children)) {
              menu.children.forEach((child: any) => {
                child.disabled = false;
              });
            }
          }
        });
      }

      setPrimaryNavItems(psmsMenus);
    } else {
      setPrimaryNavItems(getFilteredMenus(options));
    }
  };

  // Get filtered menus based on options and selected view (mimics hamburger-menu GetMenus logic)
  const updatedFilterOptions = buildFilterOptions();
  const [primaryNavItems, setPrimaryNavItems] = useState(() => {

    // Check if we're on PSMS page and return appropriate menus
    if (isPSMSPage()) {
      const isInternal = checkIsInternal();
      const loginType = isInternal ? 'Internal' : 'External';
      const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';
      const modeType = isSimulationMode ? 'Simulation' : 'General';
      const isPSMSReportCardClicked = sessionStorage.getItem('isPSMSReportCardClicked') === 'true';

      // Don't show PSMS menus for External users or in Simulation mode
      if ((loginType === 'External' || modeType === 'Simulation') && isPSMSLandingPage() && !isPSMSReportCardClicked) {
        return getFilteredMenus(updatedFilterOptions);
      }

      const initialLob = (() => {
        try {
          const saved = sessionStorage.getItem('psmsFilterState');
          if (saved) return JSON.parse(saved)?.currentFilters?.lob?.value || 'All';
        } catch { /* ignore */ }
        return 'All';
      })();
      const initialMultiTinDisabledKeys = (() => {
        try {
          if (sessionStorage.getItem('multiTinNavigation') === 'true') {
            const tins = sessionStorage.getItem('multiSelectedTins') || '';
            if (tins.split(',').length > 1) return ['digital-adoption', 'digital-adoption-summary'];
          }
          const saved = sessionStorage.getItem('psmsFilterState');
          if (saved) {
            const parsed = JSON.parse(saved);
            const tins = parsed?.currentFilters?.tin || [];
            if (Array.isArray(tins) && tins.length > 1) return ['digital-adoption', 'digital-adoption-summary'];
          }
        } catch { /* ignore */ }
        return [] as string[];
      })();
      const initialLobDisabledKeys = [
        ...(initialLob !== 'All' ? ['payment-integrity'] : []),
        ...(initialLob === 'EXC' ? ['chat-call'] : []),
        ...initialMultiTinDisabledKeys,
      ];
      const psmsMenus = getFilteredPSMSMenus({
        loginType,
        modeType,
        disabledKeys: [...initialLobDisabledKeys, ...(updatedFilterOptions.disabledKeys || [])]
      });

      const isInitiallyMultiTin =
        sessionStorage.getItem('multiTinNavigation') === 'true' &&
        (sessionStorage.getItem('multiSelectedTins') || '').split(',').filter(Boolean).length > 1;
      if (!isInitiallyMultiTin) {
        psmsMenus.forEach((menu: any) => {
          if (menu.key === 'digital-adoption') {
            menu.disabled = false;
            if (Array.isArray(menu.children)) {
              menu.children.forEach((child: any) => {
                child.disabled = false;
              });
            }
          }
        });
      }

      return psmsMenus;
    }
    return getFilteredMenus(updatedFilterOptions);
  });
  const [secondaryNavItems, setSecondaryNavItems] = useState<any>(() => {
    const currentPath = window.location.pathname;
    const isPSMS = isPSMSRoute(currentPath);

    if (isPSMS) {
      const isInternal = checkIsInternal();
      const loginType = isInternal ? 'Internal' : 'External';
      const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';

      return getPSMSFilteredSecondaryMenus({
        ...updatedFilterOptions,
        isPSMSPage: true,
        loginType: loginType,
        modeType: isSimulationMode ? 'Simulation' : 'General'
      });
    } else {
      return getSecondaryMenus(updatedFilterOptions);
    }
  });

  // Fetch and update Tax ID Number dropdown options

  // Always update Tax ID Number dropdown options after menus are recalculated
  async function injectTinDropdown(menus: any[]) {
    // Allow TIN dropdown for PSMS in simulation mode with HCO flow
    const isSimulationMode = sessionStorage.getItem('simulationMode') === 'true';
    const isHcoFlow = checkHCO();
    const isPsmsHCOFlow = JSON.parse(sessionStorage.getItem('psmsState') || '{}')?.currentFlow === 'HCO';
    const isPSMSTinFlow = JSON.parse(sessionStorage.getItem('psmsState') || '{}')?.currentFlow === 'SINGLE_TIN';
    const shouldShowTinDropdownForPSMS = isSimulationMode && (isHcoFlow || isPsmsHCOFlow || isPSMSTinFlow);
    const isPSMSExternalUser = checkIsExternal();

    const onPSMSPage = isPSMSPage();

    if (onPSMSPage && !shouldShowTinDropdownForPSMS && !isPSMSExternalUser) {
      return menus;
    }

    // For PSMS simulation mode with HCO flow, fetch TINs from providerPSMSInfo.TinDetails
    let tins: TinData[] = [];
    const isPSMScardClicked = sessionStorage.getItem('isPSMSReportCardClicked');

    // Priority 1: External users always use sessionStorage.state.tinsInfo
    if (checkIsExternal()) {
      try {
        const stateInfo = JSON.parse(sessionStorage.getItem('state') || '{}');
        if (stateInfo.tinsInfo && Array.isArray(stateInfo.tinsInfo)) {
          tins = stateInfo.tinsInfo.map((tin: any) => ({
            name: tin.TinName || tin.name || tin.TINNAME || 'Tax ID Name not available',
            value: tin.Tin || tin.value || tin.TIN || '',
            disable: tin.disable ?? false
          }));
        }

        const updatedTin = getInitialSelectedTaxId();
        if (updatedTin && updatedTin.value) {
          emitPSMSTinChangedIfNeeded({
            value: updatedTin.value,
            name: updatedTin.name,
          });
        }
      } catch (error) {
        console.error('[injectTinDropdown] Error parsing state.tinsInfo for external user:', error);
      }
    }
    // Priority 2: PSMS page with simulation + HCO flow
    else if (onPSMSPage && (shouldShowTinDropdownForPSMS && isPSMScardClicked === 'true')) {
      try {
        const providerPSMSInfo = sessionStorage.getItem('psmsFilterState');
        if (providerPSMSInfo && onPSMSPage && isPSMScardClicked === 'true') {
          const psmsInfo = JSON.parse(providerPSMSInfo);
          if (psmsInfo.taxIdOptions && Array.isArray(psmsInfo.taxIdOptions)) {
            tins = psmsInfo.taxIdOptions.map((tin: any) => ({
              name: tin.TinName || tin.name || tin.TINNAME || 'Tax ID Name not available',
              value: tin.Tin || tin.value || tin.TIN || '',
              disable: tin.disable ?? false
            }));
          }
        } else {
          const stateInfo = JSON.parse(sessionStorage.getItem('state') || '{}');
          if (stateInfo.tinsInfo && Array.isArray(stateInfo.tinsInfo)) {
            tins = stateInfo.tinsInfo.map((tin: any) => ({
              name: tin.TinName || tin.name || tin.TINNAME || 'Tax ID Name not available',
              value: tin.Tin || tin.value || tin.TIN || '',
              disable: tin.disable ?? false
            }));
          }
        }

        const updatedTin = getInitialSelectedTaxId();
        if (updatedTin && updatedTin.value) {
          emitPSMSTinChangedIfNeeded({
            value: updatedTin.value,
            name: updatedTin.name,
          });
        }

      } catch (error) {
        console.error('[injectTinDropdown] Error parsing providerPSMSInfo:', error);
      }
    }

    // Fallback to regular fetchTaxIdDropdownData if no PSMS-specific TINs found
    if (tins.length === 0) {
      tins = await fetchTaxIdDropdownData();
    }

    // Check if taxIdNumber already exists in menus
    const hasTaxIdItem = menus.some((item: any) => item.key === 'taxIdNumber');
    let updatedTin = getInitialSelectedTaxId();


    // If on PSMS page with simulation + HCO and taxIdNumber doesn't exist, add it
    if ((shouldShowTinDropdownForPSMS || checkIsExternal()) && !hasTaxIdItem) {
      const children = tins.map((tin) => ({
        key: tin.value,
        name: tin.name,
        path: '#',
        disabled: false,
      }));

      // Add taxIdNumber item after representingProvider
      const taxIdItem = {
        key: 'taxIdNumber',
        name: updatedTin?.name || selectedTinLevelData?.name || '',
        path: '#',
        disabled: false,
        hasDropdown: true,
        children: children,
      };

      const representingProviderIndex = menus.findIndex((item: any) => item.key === 'representingProvider');
      if (representingProviderIndex !== -1) {
        menus.splice(representingProviderIndex + 1, 0, taxIdItem);
      } else {
        menus.push(taxIdItem);
      }

      return menus;
    }

    if (updatedTin?.name === undefined && tins.length > 0) {
      const firstTin = tins[0];
      updateSelectedTaxId({ name: firstTin['name'] || 'Tax ID Name not available', value: firstTin['value'] || '' });
      setSelectedTinLevelData({ name: firstTin['name'] || 'Tax ID Name not available', value: firstTin['value'] || '' });
    }
    // Otherwise, update existing taxIdNumber item if it exists
    return menus.map((item: any) => {
      if (item.key === 'taxIdNumber') {
        // Create children array with all TINs
        const children = tins.map((tin) => ({
          key: tin.value,
          name: tin.name,
          path: '#',
          disabled: false,
        }));
        return {
          ...item,
          key: 'taxIdNumber',
          name: updatedTin?.name || selectedTinLevelData?.name || '',
          hasDropdown: true,
          disabled: false, // Enable the dropdown item when children are populated
          children: children,
        };
      }
      return item;
    });
  }

  // Update menus when selectedView, geo criteria, multi-TIN status, or PCOR data change
  const filterOptionsString = JSON.stringify(filterOptions);
  useEffect(() => {
    const updatedOptions = buildFilterOptions();
    updatePrimaryNavItems(updatedOptions);
    injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
  }, [
    selectedView,
    filterOptionsString,
    geoSegmentFlag,
    isMultiTin,
    geoCriteria,
    buildFilterOptions,
  ]);

  useEffect(() => {
    recordGeoCriteriaForPath(window.location.pathname, sessionStorage.getItem('GeoCriteria'));
  }, [selectedView, geoCriteria]);

  // Monitor pathname changes to update menus when navigating between PSMS and non-PSMS pages
  // This fixes the issue where PSMS menus persist after exiting Provider View
  useEffect(() => {
    const handlePathnameChange = () => {
      const newPathname = window.location.pathname;
      if (newPathname !== currentPathname) {
        setCurrentPathname(newPathname);
        // Update menus immediately when pathname changes
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
      }
    };

    // Listen for reactNavigation event (when clicking nav items)
    // Add delay to allow navigation to complete before checking pathname
    const handleReactNavigation = () => {
      setTimeout(() => {
        handlePathnameChange();
      }, 100);
    };

    window.addEventListener('reactNavigation', handleReactNavigation);

    // Listen for ExitProviderView event (when exiting Provider Mode/View)
    window.addEventListener('ExitProviderView', handlePathnameChange);

    // Listen for popstate event (browser back/forward navigation)
    window.addEventListener('popstate', handlePathnameChange);

    return () => {
      window.removeEventListener('reactNavigation', handleReactNavigation);
      window.removeEventListener('ExitProviderView', handlePathnameChange);
      window.removeEventListener('popstate', handlePathnameChange);
    };
  }, [currentPathname, buildFilterOptions]);

  // Timer-based retry mechanism for initial load - handles Angular-React timing issue
  // Angular dispatches roles asynchronously (including POCA which requires API call)
  // React renders immediately, so we need to check for role updates at intervals
  useEffect(() => {
    const timers: number[] = [];

    // Check at 100ms - catches quick dispatches

    timers.push(
      window.setTimeout(() => {
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
      }, 100)
    );

    // Check at 300ms - catches slower API responses (like POCA VIM check)
    timers.push(
      window.setTimeout(() => {
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
      }, 300)
    );

    // Check at 500ms - final safety net for slow API responses
    timers.push(
      window.setTimeout(() => {
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
      }, 500)
    );

    // Check at 800ms - request current TIN from Angular after full initialization
    // This ensures selectedTinLevelData is populated even if Tins subscription fires late
    timers.push(
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('requestCurrentTin'));
      }, 800)
    );

    // Cleanup timers on unmount
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [buildFilterOptions]); // Only run once on mount, but use latest buildFilterOptions

  // Listen to PSMS filter state changes to keep psmsLob in sync
  // PSMSMaincomponent dispatches 'psmsFilterStateChanged' whenever filters change
  useEffect(() => {
    const handlePsmsFilterChange = (event: any) => {
      const lob = event.detail?.currentFilters?.lob?.value || 'All';
      setPsmsLob(lob);
      const tins = event.detail?.currentFilters?.tin || [];
      // multiTinFromNav must require >1 TINs in session — single-TIN-in-multi-TIN-flow
      // should NOT be treated as multi-tin (would disable Digital Adoption incorrectly)
      const multiTinFromNav = sessionStorage.getItem('multiTinNavigation') === 'true' &&
        (sessionStorage.getItem('multiSelectedTins') || '').split(',').filter(Boolean).length > 1;
      setPsmsMultiTin((Array.isArray(tins) && tins.length > 1) || multiTinFromNav);
    };
    window.addEventListener('psmsFilterStateChanged', handlePsmsFilterChange);
    return () => window.removeEventListener('psmsFilterStateChanged', handlePsmsFilterChange);
  }, []);

  useEffect(() => {
    const handleExtendedTinChange = (event: any) => {
      const isSingleExtendedTinSelected = sessionStorage.getItem('psmsActiveChipOption') ? true : false;
      setExtendedTin(isSingleExtendedTinSelected);
    };
    window.addEventListener('extendedTinChanged', handleExtendedTinChange);
    return () => window.removeEventListener('extendedTinChanged', handleExtendedTinChange);
  }, []);

  useEffect(() => {
    if (isPSMSPage()) {
      const updatedOptions = buildFilterOptions();
      updatePrimaryNavItems(updatedOptions);
    }
  }, [psmsLob, psmsMultiTin, extendedTin, window.location.pathname]);

  // Update health system name in secondary nav (if needed)
  useEffect(() => {
    // Move healthSystemName inside effect to avoid dependency warning
    const healthSystemName = () => {
      let healthSystemName = getHealthCareOrgName();
      if (!checkIsInternal()) {
        healthSystemName = selectedTinLevelData?.name;
      } else if (
        checkIsSimulationMode() &&
        checkIsInternal() &&
        sessionStorage.getItem('simulationMode') === 'true'
      ) {
        healthSystemName = selectedTinLevelData?.name;
      }
      return healthSystemName;
    };
    const temp = [...secondaryNavItems];
    if (!geoSegmentFlag) {
      if (isPSMSPage()) {
        // find representingProvider in temp and update its name to health system name
        const updated = secondaryNavItems.map((item) =>
          item.key === 'representingProvider'
            ? { ...item, name: psmsHealthSystemName().toUpperCase() }
            : item
        );
        setSecondaryNavItems(updated);
      } else {
        if (temp[1]) temp[1].name = healthSystemName();
        setSecondaryNavItems(temp);
      }
    }
  }, []);

  // Listen for clicks outside NavigationBar to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const target = event.target as Element;
      // Check if click is outside the NavigationBar by checking if target is within navigation elements
      const isClickInsideNav = target.closest('[data-navigation-bar="true"]');

      if (!isClickInsideNav && openDropdown !== null) {
        setOpenDropdown(null);
        // Dispatch event to notify dropdown is closed
        const closeEvent = new CustomEvent('dropdownStateChange', {
          detail: { value: 1 },
        });
        window.dispatchEvent(closeEvent);
      }
    };

    // Listen for click events on window
    window.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdown]);

  // Allowed display values for view navigation — used to sanitize postMessage data before
  // storing in sessionStorage (prevents clear-text storage of untrusted external input).
  const ANGULAR_TO_REACT_VIEW_MAP: Record<string, string> = {
    'General': 'Provider Performance',
    'GeoSegmentation': 'Geographic Segmentation',
    'ScoreCard': 'Contract Intelligence',
    'MedicalCost': 'Affordability Drivers',
    'Dashboards': 'Operations',
    'ProviderBriefing': 'Provider Negotiation Briefing',
    'ProviderExperience': 'Provider Performance',
  };
  const ALLOWED_VIEW_DISPLAY_NAMES = new Set(Object.values(ANGULAR_TO_REACT_VIEW_MAP));

  // Listen for Angular provider dialog close events to refresh menus
  // This is ONLY for Provider Performance and Geographic Segmentation views
  useEffect(() => {
    const handleProviderDialogClosed = (event: any) => {
      // Invalidate TIN cache — provider context may have changed when dialog closes
      invalidateTinCache();

      // Check if this is an exit event (event.detail === boolean means simulation-mode toggle, not dialog close)
      if (typeof event.detail === 'boolean') {
        setActiveProviderMode(event.detail);
        if (event.detail === false) {
          setSelectedView('Provider Performance');
          setSelectedProviderPerformance('Provider Performance');
          sessionStorage.setItem('selectedProviderPerformance', 'Provider Performance');
        }
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
        return; // simulation-mode event — no dropdown update needed
      }

      if (sessionStorage.getItem('contractIdNavigation') === 'true') {
        setSelectedView('Provider Performance');
        setSelectedProviderPerformance('Provider Performance');
        setPendingViewChange(null);
        setPreviousProviderPerformance(null);
        sessionStorage.setItem('selectedProviderPerformance', 'Provider Performance');
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
        return;
      }

      if (event.detail && event.detail.selectedView) {
        const { selectedView: angularSelectedView, providerSelected } = event.detail;

        // Only use the mapped display value — never fall back to storing raw postMessage data
        const mappedDisplayValue = ANGULAR_TO_REACT_VIEW_MAP[angularSelectedView];
        const currentGeoCriteria = sessionStorage.getItem('GeoCriteria');
        const dropdownDisplayValue =
          mappedDisplayValue === 'Provider Performance' &&
            currentGeoCriteria !== null &&
            currentGeoCriteria !== 'null'
            ? 'Geographic Segmentation'
            : mappedDisplayValue ?? 'Provider Performance';

        // providerSelected=true (or cancelPopup=false) means the user confirmed the popup.
        // Use selectedNameRef.current which holds the view name the user intended to switch to.
        const isCanceled = providerSelected === false && sessionStorage.getItem('cancelPopup') !== 'false';

        if (isCanceled && previousProviderPerformance !== null) {
          setSelectedView(previousProviderPerformance);
          setSelectedProviderPerformance(previousProviderPerformance);
          if (ALLOWED_VIEW_DISPLAY_NAMES.has(previousProviderPerformance)) {
            sessionStorage.setItem('selectedProviderPerformance', previousProviderPerformance);
          }
        } else {
          // User completed the popup — prefer selectedNameRef (the clicked item) over the
          // mapped display value from Angular, since standalone flow may not carry angularSelectedView.
          const confirmedView = (selectedNameRef.current && ALLOWED_VIEW_DISPLAY_NAMES.has(selectedNameRef.current))
            ? selectedNameRef.current
            : dropdownDisplayValue;
          setSelectedView(confirmedView);
          setSelectedProviderPerformance(confirmedView);
          sessionStorage.setItem('selectedProviderPerformance', confirmedView);
        }

        // Clear pending view change and previous value
        setPendingViewChange(null);
        setPreviousProviderPerformance(null);
      } else if (pendingViewChange !== null && previousProviderPerformance !== null) {
        // No selectedView in detail — dialog closed without completing (e.g. backdrop click)
        setSelectedView(previousProviderPerformance);
        setSelectedProviderPerformance(previousProviderPerformance);
        if (ALLOWED_VIEW_DISPLAY_NAMES.has(previousProviderPerformance)) {
          sessionStorage.setItem('selectedProviderPerformance', previousProviderPerformance);
        }

        // Clear pending state
        setPendingViewChange(null);
        setPreviousProviderPerformance(null);
      }

      // Refresh both navigation bars to reflect updated state
      const updatedOptions = buildFilterOptions();
      updatePrimaryNavItems(updatedOptions);
      injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
    };

    // Listen for Angular events dispatched when provider dialog closes
    window.addEventListener('ExitProviderView', handleProviderDialogClosed);

    return () => {
      window.removeEventListener(
        'ExitProviderView',
        handleProviderDialogClosed
      );
    };
  }, [
    filterOptions,
    selectedView,
    geoSegmentFlag,
    isMultiTin,
    geoCriteria,
    pcorActive,
    pendingViewChange,
    previousProviderPerformance,
    buildFilterOptions,
  ]); // Removed refreshPcorData - it doesn't exist anymore
  // Listen for TIN changes from Angular (matches hamburger-menu TIN subscription behavior)
  // Angular dispatches 'TaxIDSyncAng' event when TIN data changes (lines 486, 495 in hamburger-menu.component.ts)
  useEffect(() => {
    const handleTinChange = (event: any) => {
      // Invalidate TIN cache — Angular has updated provider/TIN data in sessionStorage
      invalidateTinCache();
      // This matches Angular's behavior where Tins subscription triggers menu updates
      const tinUpdatedOptions = buildFilterOptions();
      updatePrimaryNavItems(tinUpdatedOptions);

      // Preserve existing TIN dropdown children during async refresh to prevent empty dropdown
      // Find the current TIN dropdown item with its children
      const currentTinDropdown = secondaryNavItems.find((item: any) => item.key === 'taxIdNumber');
      const hasExistingChildren = currentTinDropdown?.children && currentTinDropdown.children.length > 0;

      // Update menus with TIN dropdown injection
      injectTinDropdown(getSecondaryMenus(tinUpdatedOptions)).then((updatedMenus) => {
        // If we have existing children and the new menus don't have them yet (race condition)
        // preserve the existing children to prevent empty dropdown flash
        if (hasExistingChildren) {
          const newTinDropdown = updatedMenus.find((item: any) => item.key === 'taxIdNumber');
          if (newTinDropdown && (!newTinDropdown.children || newTinDropdown.children.length === 0)) {
            newTinDropdown.children = currentTinDropdown.children;
          }
        }
        setSecondaryNavItems(updatedMenus);
      });
    };

    // Listen for TIN sync event from Angular
    window.addEventListener('TaxIDSyncAng', handleTinChange);

    return () => {
      window.removeEventListener('TaxIDSyncAng', handleTinChange);
    };
  }, [buildFilterOptions, secondaryNavItems]);

  // Monitor sessionStorage for multiSelectedTins and multiTinNavigation changes
  // This handles menu updates when switching between single/multi-TIN modes
  useEffect(() => {
    const checkMultiTinChanges = () => {
      const currentMultiTinNav = sessionStorage.getItem('multiTinNavigation');
      const currentMultiTins = sessionStorage.getItem('multiSelectedTins');

      // Check if multi-TIN status changed
      const isNowMultiTin = currentMultiTinNav === 'true';
      const tinsArray = currentMultiTins ? currentMultiTins.split(',') : [];
      const isNowExtendedMultiTin = isNowMultiTin && tinsArray.length > 1;

      // If multi-TIN status changed, update menus
      if (
        isNowMultiTin !== isMultiTin ||
        isNowExtendedMultiTin !== isExtendedMultiTin
      ) {
        // Invalidate TIN cache — endpoint/payload changes with multi-TIN mode switch:
        // regular mode uses /standard-search/topfilter, multi-TIN uses /extended-tins/top-filter
        invalidateTinCache();
        const multiTinUpdatedOptions = buildFilterOptions();
        if (checkIsExternal()) {
          updatePrimaryNavItems(multiTinUpdatedOptions);
          injectTinDropdown(getSecondaryMenus(multiTinUpdatedOptions)).then(setSecondaryNavItems);
        } else {
          setPrimaryNavItems(getFilteredMenus(multiTinUpdatedOptions));
          setSecondaryNavItems(getFilteredSecondaryMenus(multiTinUpdatedOptions));
        }
      }
    };

    // Check for changes periodically (sessionStorage doesn't have native change events)
    const intervalId = setInterval(checkMultiTinChanges, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, [buildFilterOptions, isMultiTin, isExtendedMultiTin]);


  // Listen for selectedCorporateTin updates from Angular Redux store
  // This mimics hamburger-menu.component.ts line 516: this.selectedCorporateTin.subscribe((res) => {...})
  // Angular dispatches this event after Redux action SELECTED_CORPORATE_TIN_GLOBALDATA
  useEffect(() => {
    const handleCorporateTinUpdate = (event: any) => {
      if (event?.detail) {
        const updatedTin = event.detail;
        const normalizedTin = {
          value: updatedTin.value || updatedTin.Tin || '',
          name: updatedTin.name || updatedTin.TinName || ''
        };
        if (normalizedTin.value) {
          setSelectedTinLevelData(normalizedTin);

          // For External users: run PSMS association check here because the mount-time
          // effect fires before Angular has written the TIN to sessionStorage.
          // This event is the reliable signal that the TIN is now available.
          const currentLoginType = checkIsInternal() ? 'Internal' : 'External';
          if (currentLoginType === 'External') {
            setSingleViewTaxId(normalizedTin.value);
            fetchPSMSFeatureFlag().then(flagValue => {
              sessionStorage.setItem(SESSION_KEYS.PSMS_FEATURE_FLAG, flagValue);
              if (flagValue === 'Y') {
                fetchPSMSTinAssociationCheck(normalizedTin.value)
                  .then(dataToCheck => {
                    setIsPSMSTinAssociated(!!dataToCheck);
                    sessionStorage.setItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED, JSON.stringify(!!dataToCheck));
                  })
                  .catch(error => {
                    console.error('[NavigationBarExternal] Error fetching PSMS TIN association on TIN update:', error);
                  });
              }
            });
          }
        }
      }
    };

    // Listen for corporate TIN updates from Angular Redux
    window.addEventListener('SelectedCorporateTinUpdate', handleCorporateTinUpdate);

    return () => {
      window.removeEventListener('SelectedCorporateTinUpdate', handleCorporateTinUpdate);
    };
  }, []);

  // Request current TIN data from Angular on component mount
  // (requestMenuRoles removed — roles now derived directly via useMenuRoles)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('requestCurrentTin'));
  }, []);

  // Listen for TIN data updates (when switching providers in simulation mode)
  // This ensures the selected TIN value refreshes when provider changes
  useEffect(() => {
    const handleTinDataRefresh = (event?: any) => {
      // First try to get TIN from event detail (if available)
      if (event?.detail) {
        const eventTin = event.detail;
        const normalizedTin = {
          value: eventTin.value || eventTin.Tin || '',
          name: eventTin.name || eventTin.TinName || ''
        };
        if (normalizedTin.value) {
          setSelectedTinLevelData(normalizedTin);
          return;
        }
      }

      // Fallback: Refresh the selected TIN from session storage
      const updatedTin = getInitialSelectedTaxId();
      if (updatedTin && updatedTin.value) {
        setSelectedTinLevelData(updatedTin);
      }
    };

    // Listen for TaxIDSyncAng event (fired when TIN changes)
    window.addEventListener('TaxIDSyncAng', handleTinDataRefresh);

    // Listen for simulation mode provider changes
    window.addEventListener('simulationProviderChanged', handleTinDataRefresh);

    // Listen for ExitProviderView (when closing provider dialog in simulation mode)
    window.addEventListener('ExitProviderView', handleTinDataRefresh);

    return () => {
      window.removeEventListener('TaxIDSyncAng', handleTinDataRefresh);
      window.removeEventListener('simulationProviderChanged', handleTinDataRefresh);
      window.removeEventListener('ExitProviderView', handleTinDataRefresh);
    };
  }, []);

  // Monitor simulation mode changes (when "See what this provider sees" is clicked)
  // This updates the menus to show Simulation mode menus
  useEffect(() => {
    const checkSimulationMode = () => {
      const currentSimulationMode = sessionStorage.getItem('simulationMode') === 'true';
      if (currentSimulationMode !== isSimulationMode) {
        setIsSimulationMode(currentSimulationMode);
        // Invalidate TIN cache — simulation mode change means provider context changed
        invalidateTinCache();
        // Trigger menu refresh when simulation mode changes
        const updatedOptions = buildFilterOptions();
        updatePrimaryNavItems(updatedOptions);
        injectTinDropdown(getSecondaryMenus(updatedOptions)).then(setSecondaryNavItems);
      }
    };

    // Check every 200ms for simulation mode changes
    const intervalId = setInterval(checkSimulationMode, 200);

    return () => {
      clearInterval(intervalId);
    };
  }, [isSimulationMode, buildFilterOptions]);

  // Monitor URL changes to update selectedView and dropdown when navigating directly to a page
  // This handles: 1) Initial page load with direct URL, 2) Browser back/forward navigation
  // Matches Angular's router.events.subscribe behavior (hamburger-menu.component.ts lines 1323-1380)
  useEffect(() => {
    // Map of routes to their corresponding view names
    const routeToViewMap: Record<string, string> = {
      [ROUTES_PATHS.ContractIntelligence.Scorecard]: 'Contract Intelligence',
      '/medical-cost': 'Affordability Drivers',
      [ROUTES_PATHS.Operations]: 'Operations',
      [ROUTES_PATHS.Providers.Briefing]: 'Provider Negotiation Briefing',
      [ROUTES_PATHS.Providers.Experience]: 'Provider Experience',
      '/overview-page-advocate/health-system-details': 'Provider Performance'
    };

    const updateViewFromUrl = (pathname: string) => {
      const recordedGeoCriteria = getGeoCriteriaForPath(pathname);
      const currentGeoCriteria = recordedGeoCriteria ?? sessionStorage.getItem('GeoCriteria');
      if (recordedGeoCriteria !== null) {
        sessionStorage.setItem('GeoCriteria', recordedGeoCriteria);
      }
      const isGeoActive = currentGeoCriteria !== null && currentGeoCriteria !== 'null';
      const isMarketGeo = currentGeoCriteria === 'MARKET';
      const isRegionOrStateGeo = currentGeoCriteria === 'REGION' || currentGeoCriteria === 'STATE';

      // Check if current path matches any of the mapped routes
      for (const [route, viewName] of Object.entries(routeToViewMap)) {
        if (pathname === route || (pathname.startsWith(route + '/') && route !== '/')) {
          const shouldExitGeoFlow =
            viewName === 'Operations' ||
            viewName === 'Provider Negotiation Briefing' ||
            viewName === 'Provider Experience' ||
            (viewName === 'Contract Intelligence' && !isMarketGeo);

          const shouldUseGeoSegmentationView =
            (viewName === 'Provider Performance' && isGeoActive) ||
            (viewName === 'Affordability Drivers' && (isMarketGeo || isRegionOrStateGeo)) ||
            (viewName === 'Contract Intelligence' && isMarketGeo);

          if (shouldUseGeoSegmentationView) {
            setSelectedView('Geographic Segmentation');
            setSelectedProviderPerformance('Geographic Segmentation');
            sessionStorage.setItem('selectedProviderPerformance', 'Geographic Segmentation');
            return true;
          }

          // Angular parity: these reporting views should exit geo flow.
          if (isGeoActive && shouldExitGeoFlow) {
            sessionStorage.setItem('GeoCriteria', 'null');
          }

          // Default behavior: Update to the matched view name
          setSelectedView(viewName);
          setSelectedProviderPerformance(viewName);
          sessionStorage.setItem('selectedProviderPerformance', viewName);
          return true;
        }
      }
      return false;
    };

    const resetViewToCurrentPage = () => {
      const pathname = window.location.pathname;
      const recordedGeoCriteria = getGeoCriteriaForPath(pathname);
      const currentGeoCriteria = recordedGeoCriteria ?? sessionStorage.getItem('GeoCriteria');
      if (recordedGeoCriteria !== null) {
        sessionStorage.setItem('GeoCriteria', recordedGeoCriteria);
      }
      const isGeoActive = currentGeoCriteria !== null && currentGeoCriteria !== 'null';
      const fallbackView = isGeoActive ? 'Geographic Segmentation' : 'Provider Performance';
      setSelectedView(fallbackView);
      setSelectedProviderPerformance(fallbackView);
      sessionStorage.setItem('selectedProviderPerformance', fallbackView);
    };

    // Initial check on mount - handles direct URL navigation
    const currentPath = window.location.pathname;
    updateViewFromUrl(currentPath);

    // Listen to popstate event for browser back/forward navigation
    const handlePopState = () => {
      const matched = updateViewFromUrl(window.location.pathname);
      if (!matched) {
        resetViewToCurrentPage();
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Empty dependency array - run once on mount and set up listener

  const healthSystemName = () => {
    let healthSystemName = getHealthCareOrgName();
    const isInternal = checkIsInternal();
    const emulationTinLevelExperience = true; // matches environment.emulationTinLevelExperience
    const tinLevelExternalChange = true; // matches environment.tinLevelExternalChange
    // Match hamburger-menu.component.ts lines 213-219 logic exactly
    if (tinLevelExternalChange && !isInternal) {
      healthSystemName = selectedTinLevelData?.name;
    } else if (emulationTinLevelExperience && isInternal && activeProviderMode) {
      healthSystemName = selectedTinLevelData?.name;
    }
    return healthSystemName;
  };

  const psmsHealthSystemName = () => {
    const healthSystemObject = sessionStorage.getItem('providerPSMSInfo') || '{}';
    const healthSystemData = JSON.parse(healthSystemObject);
    return healthSystemData?.ProviderEntityName || '';
  }
  // Refresh Tax ID dropdown and selected TIN data
  // Called when "See what this provider sees" is clicked
  // Adds delay to allow Angular to update sessionStorage first

  const handleNavClick = (
    path: string,
    itemName: string,
    hasChildren: boolean,
    index: number,
    subIndex?: number,
    selectedRowIndex?: number,
    selectedSubIndex?: number
  ) => {
    if (hasChildren) {
      // Toggle dropdown for items with children
      const isOpening = openDropdown !== itemName;
      if (isOpening) {
        // Opening dropdown - dispatch event with value -1
        const event = new CustomEvent('dropdownStateChange', {
          detail: { value: -1 },
        });
        window.dispatchEvent(event);
      } else {
        // Closing dropdown - dispatch event with value 1
        const event = new CustomEvent('dropdownStateChange', {
          detail: { value: 1 },
        });
        window.dispatchEvent(event);
      }

      setOpenDropdown(openDropdown === itemName ? null : itemName);
    } else {
      const isHomeNavItem = itemName === 'My Home' || itemName === 'Home';

      if (
        !isHomeNavItem &&
        itemName !== 'See what this provider sees' &&
        itemName !== 'Change Providers' &&
        itemName !== NAV_ITEM_NAMES.IMPACT &&
        itemName !== 'View Tax ID Details' &&
        itemName !== 'Overview' &&
        itemName !== 'Surest' &&
        itemName !== 'Patient Care Opportunity' &&
        itemName !== 'Medical Cost Affordability' &&
        itemName !== 'Prior Auth Summary'
      ) {
        setSelectedProviderPerformance(itemName); // Update dropdown text when item clicked
      }
      if (isHomeNavItem || itemName === 'Change Providers') {
        sessionStorage.removeItem('selectedProviderPerformance');

        if (itemName === 'Change Providers') {
          // Angular behavior: always open the provider dialog, never navigate (hamburger-menu.component.ts L122-124)
          const dialogRoute = isPSMSPage() ? '/PSMS' : '/';
          if (isPSMSPage()) {
            sessionStorage.setItem('isPSMSReportCardClicked', 'true');
          }
          sessionStorage.setItem('providerDialogViewType', 'General');
          window.dispatchEvent(
            new CustomEvent('openProviderDialog', {
              detail: { route: dialogRoute, viewType: 'General' },
            })
          );
          setOpenDropdown(null);
          return;
        }
      }

      // Handle Impact button - delegate to the dedicated hook
      if (itemName === NAV_ITEM_NAMES.IMPACT) {
        setOpenDropdown(null);
        navigateImpact();
        return;
      }

      // Check if clicking the same item that was last clicked - prevent redundant actions
      if (
        lastClickedItem &&
        lastClickedItem.path === path &&
        lastClickedItem.itemName === itemName &&
        Date.now() - lastClickedItem.clickedAt < 500 &&
        itemName !== 'Change Providers' &&
        itemName !== 'View Tax ID Details' &&
        itemName !== 'See what this provider sees'
      ) {
        // Just close dropdown if open, no other action needed
        if (openDropdown !== null) {
          const event = new CustomEvent('dropdownStateChange', {
            detail: { value: 1 },
          });
          window.dispatchEvent(event);
        }
        setOpenDropdown(null);
        return;
      }

      // Update last clicked item
      setLastClickedItem({ path, itemName, clickedAt: Date.now() });

      // Navigate for items without children
      setOpenDropdown(null);

      // Special handling for "See what this provider sees" - enter simulation/provider-view mode locally
      if (itemName === 'See what this provider sees') {
        syncSimulationTinSelection();

        // Mirror Angular's simulationMode(true, path) side effects so React enters
        // provider-view mode immediately, without waiting for an Angular event.
        sessionStorage.setItem('simulationMode', 'true');
        sessionStorage.setItem('isProviderJourney', 'false');
        sessionStorage.setItem('isGeoSegMentation', 'false');
        setIsSimulationMode(true);
        setActiveProviderMode(true);

        // Compatibility events: keep the legacy listeners working during migration.
        window.dispatchEvent(
          new CustomEvent('ExitProviderView', {
            detail: true,
          })
        );

        // Angular flow also navigates to the target default page (My Insights for external).
        // Emit reactNavigation before returning so standalone React follows the same route behavior.
        if (path && path.length > 0 && path !== '#') {
          window.dispatchEvent(
            new CustomEvent('reactNavigation', {
              detail: {
                url: path,
                index,
                subIndex: subIndex !== undefined ? subIndex : -1,
                selectedRowIndex:
                  selectedRowIndex !== undefined ? selectedRowIndex : index,
                selectedSubIndex,
              },
            })
          );
        }

        return; // Don't continue with normal navigation
      }

      // Capture navigation analytics (similar to hamburger-menu leftNavgationEventCapture)
      if (path && path.length > 0 && path !== '#') {
        window.dispatchEvent(
          new CustomEvent('reactNavigation', {
            detail: {
              url: path,
              index,
              subIndex: subIndex !== undefined ? subIndex : -1,
              selectedRowIndex:
                selectedRowIndex !== undefined ? selectedRowIndex : index,
              selectedSubIndex,
            },
          })
        );
        // captureNavigationEvent(path, itemName);
      }

      // Call the callback if provided (for custom handling)
      if (onNavItemClick) {
        onNavItemClick(path, itemName);
      } else if (path !== '/ChangeProviders' && path.length > 0) {
        // Default navigation behavior - use window.location for full page load
        // window.location.href = path;
      }
    }
  };

  const handleSubItemClick = (
    path: string,
    itemName: string,
    parentIndex: number,
    childIndex: number,
    parentKey?: string,
    parentName?: string
  ) => {
    sessionStorage.setItem('cancelPopup', 'false');

    // Check if this is a Reimbursements sub-item - use pushState instead of reload
    if (path.startsWith('/Reimbursements/') || (parentKey === 'reimbursements' && path === '/Reimbursements')) {
      // Use history.pushState to change URL without reload
      window.history.pushState(null, '', path);
      // Dispatch popstate event so BrowserRouter detects the change
      window.dispatchEvent(new PopStateEvent('popstate'));
      // Close dropdown
      setOpenDropdown(null);
      return;
    }

    // Check if this is a primary navigation sub-item (not from Reporting View dropdown)
    // If so, update the Reporting View dropdown to show the parent menu name
    if (parentKey !== 'providerPerformance' && parentName) {
      // Map parent menu names to their corresponding Reporting View values
      const menuToViewMap: { [key: string]: string } = {
        'Contract Intelligence': 'Contract Intelligence',
        'Affordability Drivers': 'Affordability Drivers',
        Operations: 'Operations',
        'Provider Negotiation Briefing': 'Provider Negotiation Briefing'
      };

      const reportingViewValue = menuToViewMap[parentName];
      if (reportingViewValue) {
        setSelectedProviderPerformance(reportingViewValue);
        sessionStorage.setItem(
          'selectedProviderPerformance',
          reportingViewValue
        );
        setSelectedView(reportingViewValue); // Update selectedView for menu filtering
      }
    }

    // Additionally check if selecting the same provider performance option
    if (
      parentKey === 'providerPerformance' &&
      selectedProviderPerformance === itemName
    ) {
      // Just close the dropdown, no other action needed
      if (openDropdown !== null) {
        const event = new CustomEvent('dropdownStateChange', {
          detail: { value: 1 },
        });
        window.dispatchEvent(event);
      }
      setOpenDropdown(null);
      return;
    }

    // Update last clicked item
    setLastClickedItem({ path, itemName, clickedAt: Date.now() });

    // Closing dropdown when sub-item is clicked - dispatch event with value 1
    if (openDropdown !== null) {
      const event = new CustomEvent('dropdownStateChange', {
        detail: { value: 1 },
      });
      window.dispatchEvent(event);
    }

    setOpenDropdown(null);

    // Update the menu text and selected view for Provider Performance dropdown (like hamburger-menu changeUserView)
    if (parentKey === 'providerPerformance') {
      const isProviderPerformanceOrGeoSeg =
        itemName === 'Provider Performance' ||
        itemName === 'Geographic Segmentation';

      // Valid view names for the Reporting View dropdown
      const validViews = [
        'Provider Performance',
        'Geographic Segmentation',
        'Contract Intelligence',
        'Affordability Drivers',
        'Operations',
        'Provider Negotiation Briefing'
      ];

      if (isProviderPerformanceOrGeoSeg) {
        // Store current dropdown value before changing (to revert if popup canceled)
        setPreviousProviderPerformance(selectedProviderPerformance);
        selectedNameRef.current = itemName;
        // For Provider Performance/Geographic Segmentation:
        // - Dropdown text updated immediately (user sees change)
        // - Set pending view change (menus will update after popup closes or direct navigation)
        setPendingViewChange(itemName);
      } else {
        setSelectedProviderPerformance(itemName);
        if (validViews.includes(itemName)) {
          sessionStorage.setItem('selectedProviderPerformance', itemName);
        }
        // For other views: Update both dropdown text and menus immediately (no popup involved)
        setSelectedView(itemName); // This triggers menu filtering for other views
        // Clear stale popup state so ExitProviderView events cannot revert to old values.
        setPendingViewChange(null);
        setPreviousProviderPerformance(null);
      }

      // Note: sessionStorage persistence now handled earlier (line ~407) for ALL views
      // This ensures dropdown value persists immediately when clicked, regardless of popup scenario

      // Implement changeUserView logic from hamburger-menu
      sessionStorage.removeItem('simulationMode');
      sessionStorage.removeItem('HIVOLUMETINFLOW');

      if (itemName === 'Provider Performance') {
        // Views.General
        const isComingFromGeoSeg =
          selectedProviderPerformance === 'Geographic Segmentation';

        // Check if we're currently in geo flow (geoSegmentFlag means GeoCriteria is active)
        const isInGeoFlow = geoSegmentFlag;

        // Popup logic:
        // 1. ALWAYS show popup if coming FROM Geographic Segmentation
        // 2. Show popup if currently IN geo flow and changing from other values (Contract Intelligence, etc.) to Provider Performance
        // 3. Do NOT show popup if NOT in geo flow and coming from other views - direct route instead
        let previousOrg;
        if (sessionStorage.getItem('PreviousOrg')) {
          previousOrg = true;
        }
        const shouldShowPopup = isComingFromGeoSeg || !previousOrg;

        if (!shouldShowPopup) {
          // Direct navigation - no popup needed
          sessionStorage.setItem('selectedMappingId', 'providerData');
          const serializedState = JSON.parse(
            sessionStorage.getItem('state') || '{}'
          );
          serializedState['states'] = [];
          serializedState['region'] = 'NATIONAL';
          serializedState['mapping'] = 'PROVIDER';
          sessionStorage.setItem('state', JSON.stringify(serializedState));
          sessionStorage.setItem('isProviderJourney', 'false');
          sessionStorage.setItem('isGeoSegMentation', 'false');

          // Update dropdown text to "Provider Performance" and view immediately since no popup will be shown
          // nosemgrep: javascript.browser.security.insufficient-postmessage-origin-validation
          // This stores only view names (e.g., 'Provider Performance'), not sensitive data
          sessionStorage.setItem(
            'selectedProviderPerformance',
            'Provider Performance'
          );
          setSelectedProviderPerformance('Provider Performance'); // Update dropdown display text
          setSelectedView('Provider Performance'); // Use display name for menu filtering

          // Update menus immediately for direct navigation to Provider Performance view
          const directNavOptions = buildFilterOptions();
          updatePrimaryNavItems(directNavOptions);
          injectTinDropdown(getSecondaryMenus(directNavOptions)).then(setSecondaryNavItems);
          // Clear pending state since we're handling navigation without popup
          setPendingViewChange(null);
          setPreviousProviderPerformance(null);

          // Dispatch event to notify about view change
          window.dispatchEvent(
            new CustomEvent('reactNavigation', {
              detail: {
                url: path,
                index: parentIndex,
                subIndex: childIndex,
                selectedRowIndex: parentIndex,
                selectedSubIndex: childIndex,
                viewChange: true,
                selectedView: itemName,
              },
            })
          );

          if (path && path.length > 0) {
            // window.location.href = path;
          }
        } else {
          // Show popup - coming from Geographic Segmentation and no provider selected
          sessionStorage.setItem('providerDialogViewType', 'General');
          window.dispatchEvent(
            new CustomEvent('openProviderDialog', {
              detail: { route: path, viewType: 'General' },
            })
          );
          sessionStorage.setItem('isProviderJourney', 'false');
          sessionStorage.setItem('isGeoSegMentation', 'false');
        }
      } else if (itemName === 'Geographic Segmentation') {
        // Views.GeoSegmentation - ALWAYS show popup
        sessionStorage.setItem('providerDialogViewType', 'GeoSegmentation');
        sessionStorage.setItem('isProviderJourney', 'false');

        // Always open Modal for geo segmentation - dispatch custom event
        window.dispatchEvent(
          new CustomEvent('openProviderDialog', {
            detail: { route: path, viewType: 'GeoSegmentation' },
          })
        );
      } else {
        const currentGeoCriteria = sessionStorage.getItem('GeoCriteria');
        const isMarketGeo = currentGeoCriteria === 'MARKET';
        const shouldExitGeoFlow =
          itemName === 'Operations' ||
          itemName === 'Provider Negotiation Briefing' ||
          itemName === 'Provider Experience' ||
          (itemName === 'Contract Intelligence' && !isMarketGeo);

        if (shouldExitGeoFlow) {
          sessionStorage.setItem('GeoCriteria', 'null');
        }

        // Other views (ScoreCard, MedicalCost, etc.)
        window.dispatchEvent(
          new CustomEvent('reactNavigation', {
            detail: {
              url: path,
              index: parentIndex,
              subIndex: childIndex,
              selectedRowIndex: parentIndex,
              selectedSubIndex: childIndex,
              viewChange: true,
              selectedView: itemName,
            },
          })
        );

        // if (path && path.length > 0) {
        //   window.location.href = path;
        // }
      }

      return; // Don't continue with normal navigation for provider performance items
    }

    // Special handling for "See what this provider sees" - dispatch custom event instead of navigation
    if (itemName === 'See what this provider sees') {
      setSelectedView('See what this provider sees');
    }

    // PSMS Summary navigation: store target section in sessionStorage
    // so PSMSMaincomponent can scroll to and expand the correct card.
    // Maps the parent menu key to the section key used by ProviderScorecardSections.
    if (path === '/PSMS' && itemName === 'Summary' && parentKey) {
      const psmsMenuKeyToSectionKey: Record<string, string> = {
        'psms-claims': 'claims',
        'digital-adoption': 'digitalAdoption',
        'prior-auth': 'priorAuthorization',
        'payment-integrity': 'paymentIntegrity',
        'accounts-receivable': 'accountsReceivable',
        'chat-call': 'chatCall',
        'inquiries-issues-routing': 'inquiryIssuesRouting',
        'prc': 'providerResolutionCenter',
      };
      const sectionKey = psmsMenuKeyToSectionKey[parentKey];
      if (sectionKey) {
        sessionStorage.setItem('psms-target-section', sectionKey);
      }
    }

    // Capture navigation analytics (similar to hamburger-menu leftNavgationEventCapture)
    if (path && path.length > 0) {
      window.dispatchEvent(
        new CustomEvent('reactNavigation', {
          detail: {
            url: path,
            index: parentIndex,
            subIndex: childIndex,
            selectedRowIndex: parentIndex,
            selectedSubIndex: childIndex,
          },
        })
      );
      //  captureNavigationEvent(path, itemName);
    }

    // Call the callback if provided (for custom handling)
    if (onNavItemClick) {
      onNavItemClick(path, itemName);
    } else if (path && path.length > 0) {
      // Default navigation behavior - use window.location for full page load
      //  window.location.href = path;
    }
  };

  const getSecondaryNavItemLabel = (item: any) => {
    if (item.key === 'providerPerformance') {
      return selectedProviderPerformance;
    }
    if (item.key === 'representingProvider') {
      return isContractIdNav
        ? item.name
        : isMultiTin
          ? groupName
          : !isPSMSPage()
            ? healthSystemName() || item.name
            : psmsHealthSystemName().toUpperCase();
    }
    if (item.key === 'corporateTaxIdOwner') {
      return selectedTinLevelData?.name || item.name;
    }
    if (item.key === 'taxIdNumber') {
      return selectedTinLevelData?.value || item.name;
    }
    return item.name;
  };

  const handleMobileNavClick = (path: string, itemName: string, hasChildren: boolean, index: number) => {
    handleNavClick(path, itemName, hasChildren, index);
    if (!hasChildren) {
      setMobileDrawerOpen(false);
    }
  };

  const handleMobileSubItemClick = (
    path: string,
    itemName: string,
    parentIndex: number,
    childIndex: number,
    parentKey?: string,
    parentName?: string
  ) => {
    handleSubItemClick(path, itemName, parentIndex, childIndex, parentKey, parentName);
    setMobileDrawerOpen(false);
  };

  const renderMobileChildLinks = (children: any[], parentIndex: number, parentKey?: string, parentName?: string) => (
    <div style={styles.mobileDrawerChildren}>
      {children.map((child, childIndex) => (
        <div key={`${parentKey || parentName || parentIndex}-${childIndex}`} style={styles.mobileDrawerChildGroup}>
          {child.children && child.children.length > 0 ? (
            <>
              <div style={styles.mobileDrawerSectionLabel}>{child.name}</div>
              {renderMobileChildLinks(child.children, parentIndex, parentKey, parentName)}
            </>
          ) : (
            <button
              type="button"
              style={{
                ...styles.mobileDrawerChildButton,
                ...(child.disabled ? styles.mobileDrawerDisabled : {}),
              }}
              onClick={() => handleMobileSubItemClick(child.path, child.name, parentIndex, childIndex, parentKey, parentName)}
              disabled={child.disabled}
            >
              {child.name}
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const renderMobileMenuSection = (title: string, items: any[]) => (
    <div style={styles.mobileDrawerSection}>
      <div style={styles.mobileDrawerSectionTitle}>{title}</div>
      {items.map((item, index) => {
        const label = title === 'Secondary' ? getSecondaryNavItemLabel(item) : item.name;
        return (
          <div key={`${title}-${item.key || item.name}-${index}`} style={styles.mobileDrawerItem}>
            <button
              type="button"
              style={{
                ...styles.mobileDrawerParentButton,
                ...(item.disabled ? styles.mobileDrawerDisabled : {}),
              }}
              onClick={() => handleMobileNavClick(item.path, item.name, !!item.children, index)}
              disabled={item.disabled}
            >
              <span>{label}</span>
              {item.children && <span style={styles.mobileDrawerChevron}>›</span>}
            </button>
            {item.children && renderMobileChildLinks(item.children, index, item.key, item.name)}
          </div>
        );
      })}
    </div>
  );

  // const handleSearchChange = (e) => {
  //   setSearchValue(e.target.value);
  // };

  return (
    <div data-navigation-bar="true" style={styles.navigationBarRedesign}>
      {/* Header Container */}
      <div style={styles.headerContainer}>
        <div style={{ ...styles.header, ...(isPSMSPage() ? { marginTop: '0px' } : {}) }}>
          {/* Main Nav */}

          {isMobileNav && mobileDrawerOpen && (
            <div style={styles.mobileDrawerOverlay} onClick={() => setMobileDrawerOpen(false)}>
              <div
                style={styles.mobileDrawer}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
                onClick={(event) => event.stopPropagation()}
              >
                <div style={styles.mobileDrawerHeader}>
                  <span style={styles.mobileDrawerTitle}>Navigation</span>
                  <button
                    type="button"
                    style={styles.mobileDrawerCloseButton}
                    onClick={() => setMobileDrawerOpen(false)}
                    aria-label="Close navigation menu"
                  >
                    ×
                  </button>
                </div>
                <div style={styles.mobileDrawerContent}>
                  {renderMobileMenuSection('Primary', primaryNavItems)}
                  {secondaryNavItems.length > 0 && renderMobileMenuSection('Secondary', secondaryNavItems)}
                </div>
              </div>
            </div>
          )}

          {/* Top Level Nav - Primary */}
          <div style={{ ...styles.topLevelNav, ...(isMobileNav ? styles.desktopNavHidden : {}) }}>
            <div style={styles.navItems}>
              {primaryNavItems.map((item, index) => (
                <div key={index} style={styles.navItemsContainer}>
                  <div style={styles.flyoutMenu}>
                    <button
                      style={styles.button}
                      onClick={() =>
                        handleNavClick(
                          item.path,
                          item.name,
                          !!item.children,
                          index
                        )
                      }
                      disabled={item.disabled}
                    >
                      <span style={styles.buttonText}>{item.name}</span>
                      {item.hasDropdown && (
                        <div style={styles.expandMoreWhite}>
                          {openDropdown === item.name ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.94 10.2734L8 7.22011L11.06 10.2734L12 9.33344L8 5.33344L4 9.33344L4.94 10.2734Z"
                                fill="#FFFFFF"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.94 5.72656L8 8.77989L11.06 5.72656L12 6.66656L8 10.6666L4 6.66656L4.94 5.72656Z"
                                fill="#FFFFFF"
                              />
                            </svg>
                          )}
                        </div>
                      )}
                    </button>

                    {/* Dropdown Menu for Children - Figma Style */}
                    {item.children && openDropdown === item.name && (
                      <div style={styles.dropdownMenu}>
                        {/* Triangle pointer pointing to clicked menu item */}
                        <div style={styles.dropdownTriangle}>
                          <svg
                            width="22"
                            height="11"
                            viewBox="0 0 22 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={styles.triangleSvg}
                          >
                            <path d="M11 0L22 11H0L11 0Z" fill="#FFFFFF" />
                          </svg>
                        </div>

                        <div style={styles.dropdownColumns}>
                          <div style={styles.dropdownColumn}>
                            {/* Links container - No heading, just options */}
                            <div style={styles.dropdownLinks}>
                              {/* Non-clickable section header (exclude Provider Performance dropdown) */}
                              {item.key !== 'providerPerformance' && (
                                <div style={styles.dropdownHeaderItem}>
                                  <span style={styles.dropdownHeaderText}>
                                    {item.name}
                                  </span>
                                </div>
                              )}
                              {item.children.map((child, childIndex) => (
                                <div
                                  key={childIndex}
                                  style={styles.dropdownItem}
                                >
                                  {/* If child has nested children, render as non-clickable header */}
                                  {child.children &&
                                    child.children.length > 0 ? (
                                    <div
                                      style={{
                                        ...styles.dropdownLink,
                                        cursor: 'default',
                                      }}
                                    >
                                      <span
                                        style={{
                                          ...styles.dropdownItemText,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {child.name}
                                      </span>
                                    </div>
                                  ) : (
                                    <button
                                      style={{
                                        ...styles.dropdownLink,
                                        ...(child.disabled
                                          ? styles.dropdownItemDisabled
                                          : {}),
                                      }}
                                      onClick={() =>
                                        handleSubItemClick(
                                          child.path,
                                          child.name,
                                          index,
                                          childIndex,
                                          item.key,
                                          item.name
                                        )
                                      }
                                      disabled={child.disabled}
                                    >
                                      <span
                                        style={styles.dropdownItemText}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.textDecoration =
                                            'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.textDecoration =
                                            'none';
                                        }}
                                      >
                                        {child.name}
                                      </span>
                                    </button>
                                  )}

                                  {/* Nested Children */}
                                  {child.children && (
                                    <div style={styles.nestedDropdown}>
                                      {child.children.map(
                                        (nestedChild, nestedIndex) => (
                                          <div
                                            key={nestedIndex}
                                            style={styles.dropdownItem}
                                          >
                                            <button
                                              style={{
                                                ...styles.dropdownLink,
                                                ...(nestedChild.disabled
                                                  ? styles.dropdownItemDisabled
                                                  : {}),
                                              }}
                                              onClick={() =>
                                                handleSubItemClick(
                                                  nestedChild.path,
                                                  nestedChild.name,
                                                  index,
                                                  childIndex,
                                                  item.key,
                                                  item.name
                                                )
                                              }
                                              disabled={nestedChild.disabled}
                                            >
                                              <span
                                                style={styles.dropdownItemText}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.textDecoration =
                                                    'underline';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.textDecoration =
                                                    'none';
                                                }}
                                              >
                                                {nestedChild.name}
                                              </span>
                                            </button>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Level Nav - Secondary */}
          <div style={{ ...styles.topLevelNavSecondary, ...(isMobileNav ? styles.desktopNavHidden : {}) }}>
            <div style={styles.navItemsSecondary}>
              {secondaryNavItems.map((item, index) => (
                <div key={index} style={styles.navItemsContainerSecondary}>
                  <div
                    style={{
                      ...styles.flyoutMenuSecondary,
                      position: 'relative',
                    }}
                  >
                    <button
                      style={styles.buttonSecondary}
                      onClick={() =>
                        handleNavClick(
                          item.path,
                          item.name,
                          !!item.children,
                          index
                        )
                      }
                      disabled={item.disabled}
                    >
                      <div style={{ display: 'block' }}>
                        {item.key === 'providerPerformance' && (
                          <div style={styles.providerPerformanceLabel}>
                            Reporting View
                          </div>
                        )}
                        {item.key === 'representingProvider' && (
                          <div style={styles.providerLabel}>
                            You are representing
                          </div>
                        )}
                        {item.key === 'corporateTaxIdOwner' && (
                          <div style={styles.providerLabel}>
                            Corporate Tax ID Owner
                          </div>
                        )}
                        {item.key === 'taxIdNumber' && (
                          <div style={styles.providerLabel}>Tax ID Number</div>
                        )}
                        <span
                          style={{
                            ...styles.buttonTextSecondary,
                            ...(item.key === 'representingProvider' ||
                              item.key === 'corporateTaxIdOwner'
                              ? { fontWeight: 700 }
                              : {}),
                          }}
                        >
                          {item.key === 'providerPerformance'
                            ? selectedProviderPerformance
                            : item.key === 'representingProvider'
                              ? isContractIdNav
                                ? item.name
                                : isMultiTin
                                  ? groupName
                                  : !isPSMSPage() ? healthSystemName() || item.name : psmsHealthSystemName().toUpperCase()
                              : item.key === 'corporateTaxIdOwner'
                                ? selectedTinLevelData?.name || item.name
                                : item.key === 'taxIdNumber'
                                  ? selectedTinLevelData?.value || item.name
                                  : item.name}
                        </span>
                        {/* Display single TaxId when applicable (like hamburger-menu lines 157-159) */}
                        {/* Skip for Contract ID navigation - the ID is already part of item.name above, avoiding a duplicate display */}
                        {item.key === 'representingProvider' &&
                          !isContractIdNav &&
                          showSingleTaxId &&
                          singleViewTaxId && (
                            <div style={styles.singleTaxIdLabel}>
                              {singleViewTaxId}
                            </div>
                          )}
                      </div>
                      {item.hasDropdown && (
                        <div style={styles.expandMoreWhiteSecondary}>
                          {openDropdown === item.name ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.94 10.2734L8 7.22011L11.06 10.2734L12 9.33344L8 5.33344L4 9.33344L4.94 10.2734Z"
                                fill="#FFFFFF"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.94 5.72656L8 8.77989L11.06 5.72656L12 6.66656L8 10.6666L4 6.66656L4.94 5.72656Z"
                                fill="#FFFFFF"
                              />
                            </svg>
                          )}
                        </div>
                      )}
                    </button>

                    {/* Dropdown Menu for Secondary Navigation */}
                    {item.children && openDropdown === item.name && (
                      <div style={{
                        ...styles.dropdownMenu,
                        top: '49px',
                        ...(item.key === 'taxIdNumber' ? { padding: '0px' } : {})
                      }}>
                        {/* Triangle pointer pointing to clicked menu item */}
                        <div style={styles.dropdownTriangle}>
                          <svg
                            width="22"
                            height="11"
                            viewBox="0 0 22 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={styles.triangleSvg}
                          >
                            <path d="M11 0L22 11H0L11 0Z" fill="#FFFFFF" />
                          </svg>
                        </div>

                        <div style={styles.dropdownColumns}>
                          <div style={styles.dropdownColumn}>
                            {/* Links container - No heading, just options */}
                            <div
                              style={{
                                ...styles.dropdownLinks,
                                ...(item.key === 'taxIdNumber' ? {
                                  maxHeight: '280px',
                                  overflowY: 'auto',
                                  padding: '0px', // Remove 24px outside padding
                                  gap: '0px', // Remove 8px gap between options
                                } : {})
                              }}
                            >
                              {/* Remove header for Tax ID dropdown, keep for others */}
                              {item.key !== 'taxIdNumber' && (
                                <div style={styles.dropdownHeaderItem}>
                                  <span style={styles.dropdownHeaderText}>
                                    {item.key === 'providerPerformance'
                                      ? 'Reporting View'
                                      : item.name}
                                  </span>
                                </div>
                              )}
                              {item.key === 'taxIdNumber' && item.children && item.children.length > 0
                                ? item.children.map((child, childIndex) => (
                                  <div
                                    key={childIndex}
                                    style={styles.dropdownItem}
                                  >
                                    <button
                                      style={{
                                        ...styles.taxIdDropdownOption,
                                        ...(selectedTinLevelData?.value === child.key
                                          ? {
                                            ...styles.taxIdDropdownOptionSelected,
                                            backgroundColor: '#e3f0fd',
                                          }
                                          : {}),
                                        ...(child.disabled
                                          ? styles.dropdownItemDisabled
                                          : {}),
                                      }}
                                      onClick={() => {
                                        // Create selected TIN object
                                        const selectedTin = {
                                          value: child.key,
                                          name: child.name
                                        };

                                        // Update local state immediately to show selection in UI
                                        setSelectedTinLevelData(selectedTin);

                                        // Write new TIN to all session/state keys so every page reads fresh data on reload
                                        sessionStorage.setItem('globalfilterReset', 'true');
                                        updateSelectedTaxId(selectedTin);

                                        // PSMS TIN association check (simulation HCO or external)
                                        const geoSegmentFlagCheck = geoCriteria !== 'null' && geoCriteria !== null;
                                        const needsPSMSCheck =
                                          (isSimulationMode && checkHCO() && !geoSegmentFlagCheck) ||
                                          loginType === 'External';
                                        if (needsPSMSCheck) {
                                          fetchPSMSFeatureFlag().then(flagValue => {
                                            sessionStorage.setItem(SESSION_KEYS.PSMS_FEATURE_FLAG, flagValue);
                                            if (flagValue === 'Y') {
                                              fetchPSMSTinAssociationCheck(selectedTin.value)
                                                .then(dataToCheck => {
                                                  setIsPSMSTinAssociated(!!dataToCheck);
                                                  sessionStorage.setItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED, JSON.stringify(!!dataToCheck));
                                                })
                                                .catch(error => {
                                                  console.error('[NavigationBar] Error fetching PSMS TIN association:', error);
                                                });
                                            }
                                          });
                                        }

                                        // Close dropdown
                                        setOpenDropdown(null);

                                        // Gold Card requires session flags before reload so GC Main.tsx
                                        // routes to summary (not cover page) after mount.
                                        const currentPath = window.location.pathname;
                                        const isGoldCardPage = currentPath.includes('/prior-auth/gold-card');
                                        const isSimulationOrExternal = checkIsSimulationMode() || !checkIsInternal();
                                        if (isGoldCardPage && isSimulationOrExternal) {
                                          sessionStorage.setItem('fromExternalCoverPage', 'true');
                                          sessionStorage.removeItem('fromHCOCoverPage');
                                          sessionStorage.removeItem('fromSingleTinCoverPage');
                                          sessionStorage.setItem('gcCoverPageSelectedTin', selectedTin.value);
                                          sessionStorage.setItem('gcCoverPageSelectedTinName', selectedTin.name);
                                        }

                                        // Hard reload for every route — all pages read selectedCorporateTin
                                        // from sessionStorage on mount, so reload guarantees fresh data.
                                        window.location.reload();
                                      }}
                                      disabled={child.disabled}
                                      onMouseEnter={(e) => {
                                        if (!child.disabled && selectedTinLevelData?.value !== child.key) {
                                          e.currentTarget.style.backgroundColor = '#F7F7F7';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (selectedTinLevelData?.value !== child.key) {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                      }}
                                    >
                                      <span style={{
                                        ...styles.taxIdDropdownOptionValue,
                                        ...(selectedTinLevelData?.value === child.key
                                          ? styles.taxIdDropdownOptionValueSelected
                                          : {})
                                      }}>
                                        {child.key}
                                      </span>
                                      <span style={styles.taxIdDropdownOptionName}>
                                        {child.name}
                                      </span>
                                    </button>
                                  </div>
                                ))
                                : item.children && item.children.length > 0 && item.children.map((child, childIndex) => (
                                  <div
                                    key={childIndex}
                                    style={styles.dropdownItem}
                                  >
                                    {/* If child has nested children, render as non-clickable header */}
                                    {child.children &&
                                      child.children.length > 0 ? (
                                      <div
                                        style={{
                                          ...styles.dropdownLink,
                                          cursor: 'default',
                                        }}
                                      >
                                        <span
                                          style={{
                                            ...styles.dropdownItemText,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {child.name}
                                        </span>
                                      </div>
                                    ) : (
                                      <button
                                        style={{
                                          ...styles.dropdownLink,
                                          ...(child.disabled
                                            ? styles.dropdownItemDisabled
                                            : {}),
                                        }}
                                        onClick={() =>
                                          handleSubItemClick(
                                            child.path,
                                            child.name,
                                            index,
                                            childIndex,
                                            item.key
                                          )
                                        }
                                        disabled={child.disabled}
                                      >
                                        <span
                                          style={styles.dropdownItemText}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.textDecoration =
                                              'underline';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.textDecoration =
                                              'none';
                                          }}
                                        >
                                          {child.name}
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <ImpactModal />
    </div>
  );
};

export default NavigationBar;